import React from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { AddBoxOutlined, IndeterminateCheckBoxOutlined } from '@material-ui/icons';
import { TreeItem, TreeView } from '@material-ui/lab';
import * as _ from 'lodash';
import LabelWithCheckbox from '../custom-components/LabelWithCheckbox';
import { allItemIds, PunchListExportTreeLocation } from './punch-list-export-utils';

// The same look as DocumentTypeTreeView, which the project export dialog picks with.
const useStyles = makeStyles(() =>
  createStyles({
    treeItemRoot: {
      '&:focus > .MuiTreeItem-content .MuiTreeItem-label': {
        backgroundColor: 'transparent',
      },
      '&.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label': {
        backgroundColor: 'transparent',
      },
      '&.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label:hover, &.Mui-selected:focus > .MuiTreeItem-content .MuiTreeItem-label': {
        backgroundColor: 'transparent',
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    treeItemLabel: {
      whiteSpace: 'nowrap',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    treeItemGroup: {
      marginTop: 4,
    },
    parentTreeItemLabel: {
      paddingLeft: 0,
    },
  }),
);

type PunchListLocationTreeViewProps = {
  tree: PunchListExportTreeLocation[];
  selectedItemIds: string[];
  setSelectedItemIds: React.Dispatch<React.SetStateAction<string[]>>;
  /** Shows a selection without letting it be changed, for looking at a past export. */
  disabled?: boolean;
};

/**
 * Punch list locations with their items underneath, and one box above them all. Ticking a location
 * ticks every item in it, as ticking a location row does in the log table.
 */
export default function PunchListLocationTreeView(props: PunchListLocationTreeViewProps) {
  const { tree, selectedItemIds, setSelectedItemIds, disabled } = props;
  const classes = useStyles();

  const everyItemId = allItemIds(tree);
  const selectedCount = everyItemId.filter((id) => selectedItemIds.includes(id)).length;

  return (
    <TreeView
      defaultCollapseIcon={<IndeterminateCheckBoxOutlined />}
      defaultExpandIcon={<AddBoxOutlined />}
      style={{ padding: 6, border: '1px solid #949494', borderRadius: '4px' }}
    >
      <TreeItem
        nodeId="select-all"
        label={
          <LabelWithCheckbox
            label="Select all"
            checked={everyItemId.length > 0 && selectedCount === everyItemId.length}
            indeterminate={selectedCount > 0 && selectedCount < everyItemId.length}
            disabled={disabled}
            onChange={(_event, checked) => setSelectedItemIds(checked ? everyItemId : [])}
          />
        }
        classes={{ label: classes.parentTreeItemLabel }}
        style={{ marginTop: 2, marginBottom: 2 }}
      />
      {tree.map((location) => {
        const itemIds = location.items.map((item) => item.id);
        const selectedHere = itemIds.filter((id) => selectedItemIds.includes(id)).length;
        return (
          <TreeItem
            key={location.id}
            nodeId={location.id}
            label={
              <LabelWithCheckbox
                label={`${location.label} (${location.items.length})`}
                checked={selectedHere === itemIds.length}
                indeterminate={selectedHere > 0 && selectedHere < itemIds.length}
                disabled={disabled}
                onChange={(_event, checked) =>
                  checked
                    ? setSelectedItemIds((prev) => _.uniq([...prev, ...itemIds]))
                    : setSelectedItemIds((prev) => prev.filter((id) => !itemIds.includes(id)))
                }
              />
            }
            classes={{ label: classes.parentTreeItemLabel }}
            style={{ marginTop: 2, marginBottom: 2 }}
          >
            {location.items.map((item) => (
              <TreeItem
                key={item.id}
                nodeId={item.id}
                label={
                  <LabelWithCheckbox
                    label={item.label}
                    checked={selectedItemIds.includes(item.id)}
                    disabled={disabled}
                    onChange={(_event, checked) =>
                      checked
                        ? setSelectedItemIds((prev) => _.uniq([...prev, item.id]))
                        : setSelectedItemIds((prev) => prev.filter((id) => id !== item.id))
                    }
                  />
                }
                classes={{
                  root: classes.treeItemRoot,
                  label: classes.treeItemLabel,
                  group: classes.treeItemGroup,
                }}
                style={{ marginTop: 2, marginBottom: 2 }}
              />
            ))}
          </TreeItem>
        );
      })}
    </TreeView>
  );
}
